import { ANONYMOUS_CONTEXT_KEY } from '@/constants/launchDarkly';

type GuestIdentityInput = {
  identifyGuests: boolean;
  anonymousId: string | undefined;
};

export type GuestIdentity = {
  key: string;
  /** Undefined while collapsed, so `buildLdContext` cannot prefer it. */
  anonymousId: string | undefined;
  /** Whether `key` is a stable randomization unit. */
  isIdentified: boolean;
};

/** One shared key, no anonymous ID, not a randomization unit. */
export const COLLAPSED_GUEST_IDENTITY: GuestIdentity = {
  key: ANONYMOUS_CONTEXT_KEY,
  anonymousId: undefined,
  isIdentified: false,
};

/**
 * Decides which key a guest's context uses. Collapsed unless the flag is on
 * AND an anonymous ID exists — a first-ever visitor has none yet, and claiming
 * `isIdentified` on the shared key would attribute an exposure to a non-unit.
 */
export const resolveGuestIdentity = ({
  identifyGuests,
  anonymousId,
}: GuestIdentityInput): GuestIdentity => {
  if (!identifyGuests || !anonymousId) return COLLAPSED_GUEST_IDENTITY;

  return { key: anonymousId, anonymousId, isIdentified: true };
};

/**
 * Everything except the shared guest key is a randomization unit. The flag
 * governs guest contexts only, so a signed-in customer stays identified while
 * it is off — otherwise flipping it off would disable logged-in experiments.
 */
export const isRandomizationUnitKey = (key: string | undefined): boolean =>
  Boolean(key) && key !== ANONYMOUS_CONTEXT_KEY;
